import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Paper, Stack } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import PageHeader from "../../../../components/common/PageHeader";
import ActionIconButton from "../../../../components/common/ActionIconButton";
import PageLoader from "../../../../components/common/PageLoader";
import ChipList from "../../../../components/common/ChipList";
import TruncatedText from "../../../../components/common/TruncatedText";
import EmptyState from "../../../../components/common/EmptyState";
import SearchFilterBar from "../../../../components/common/SearchFilterBar";
import DataTable from "../../../../components/DataTable";
import { appActions } from "../../../../redux/actions";
import { getApiErrorMessage } from "../../../../helpers";
import { ROLE_SCOPES } from "../../constants";
import { systemRoleService, tenantRoleService } from "../../services";
import useAuth from "../../../../hooks/useAuth";
import {
  collectAllowSuggestions,
  matchesRoleSearch,
  normalizeRoleList,
} from "../../utils/normalizeRole";
import RoleFormDialog from "../RoleFormDialog";
import { tenantPermissionService } from "../../../permission-management/services";
import { normalizePermissionList } from "../../../permission-management/utils/normalizePermission";

const RoleManagement = ({
  scope = ROLE_SCOPES.SYSTEM,
  roleService: roleApi,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { activeTenantId, tenantId } = useAuth();
  const isSystem = scope === ROLE_SCOPES.SYSTEM;
  const requestTenantId = activeTenantId || tenantId;
  const listBase = isSystem ? "/roles" : "/tenant-roles";
  const api = roleApi || (isSystem ? systemRoleService : tenantRoleService);

  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [nameFilter, setNameFilter] = useState("");
  const [appliedName, setAppliedName] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [permissionOptions, setPermissionOptions] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(false);

  const allowSuggestions = useMemo(() => {
    const fromRoles = collectAllowSuggestions(roles);
    return Array.from(
      new Set([...(permissionOptions || []), ...fromRoles].filter(Boolean))
    ).sort();
  }, [permissionOptions, roles]);

  const loadPermissionOptions = useCallback(async () => {
    if (isSystem) {
      setPermissionOptions([]);
      return;
    }
    setPermissionsLoading(true);
    try {
      const payload = await tenantPermissionService.getAllTenantPermissions(
        requestTenantId
      );
      setPermissionOptions(
        normalizePermissionList(payload)
          .map((item) => item.name)
          .filter(Boolean)
      );
    } catch {
      setPermissionOptions([]);
    } finally {
      setPermissionsLoading(false);
    }
  }, [isSystem, requestTenantId]);

  const loadRoles = useCallback(
    async (name = appliedName) => {
      setLoading(true);
      setError("");
      try {
        const payload = isSystem
          ? await (api?.getAllSystemRoles || systemRoleService.getAllSystemRoles)()
          : await tenantRoleService.getAllTenantRoles(requestTenantId);
        const normalized = normalizeRoleList(payload).filter((role) =>
          matchesRoleSearch(role, { name })
        );
        setRoles(normalized);
      } catch (err) {
        const message = getApiErrorMessage(err);
        setRoles([]);
        setError(message);
        dispatch(appActions.apiErrorMessage(message));
      } finally {
        setLoading(false);
      }
    },
    [api, appliedName, dispatch, isSystem, requestTenantId]
  );

  useEffect(() => {
    loadRoles(appliedName);
  }, [appliedName, loadRoles]);

  const openDetails = useCallback(
    (row) => {
      const id = row?.roleId || row?.name;
      if (!id) return;
      navigate(`${listBase}/view/${encodeURIComponent(id)}`, {
        state: { role: row },
      });
    },
    [listBase, navigate]
  );

  const openCreate = async () => {
    setFormOpen(true);
    if (!isSystem) return;
    await loadPermissionOptions();
  };

  const handleCreate = async (payload) => {
    setSaving(true);
    try {
      if (isSystem) {
        await (api?.createSystemRole || systemRoleService.createSystemRole)(
          payload
        );
      } else {
        await tenantRoleService.createTenantRole(payload, requestTenantId);
      }
      dispatch(appActions.apiSuccessMessage("Role created successfully."));
      setFormOpen(false);
      await loadRoles(appliedName);
    } catch (err) {
      throw new Error(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Role Name",
        flex: 1,
        minWidth: 160,
        renderCell: (params) => <TruncatedText value={params.value} />,
      },
      {
        field: "allow",
        headerName: "Permissions",
        flex: 1.6,
        minWidth: 220,
        sortable: false,
        valueGetter: (value, row) =>
          Array.isArray(row?.allow) ? row.allow.join(", ") : "",
        renderCell: (params) => (
          <ChipList items={params.row.allow} maxVisible={2} />
        ),
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 90,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <ActionIconButton
            title="View Details"
            icon={<VisibilityOutlinedIcon fontSize="small" />}
            onClick={() => openDetails(params.row)}
          />
        ),
      },
    ],
    [openDetails]
  );

  return (
    <Box sx={{ width: "100%" }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        spacing={1.5}
        sx={{ mb: 1 }}
      >
        <PageHeader
          title="Roles"
          subtitle={
            isSystem
              ? "Manage system roles and their permissions."
              : "Manage tenant roles and their permissions."
          }
          sx={{ mb: 0 }}
        />
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            startIcon={<AddOutlinedIcon />}
            onClick={openCreate}
          >
            Add Role
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshOutlinedIcon />}
            onClick={() => loadRoles(appliedName)}
            disabled={loading}
          >
            Refresh
          </Button>
        </Stack>
      </Stack>

      <SearchFilterBar
        fields={[
          {
            key: "name",
            label: "Role Name",
            value: nameFilter,
            onChange: (e) => setNameFilter(e.target.value),
            width: 280,
          },
        ]}
        onSearch={() => setAppliedName(nameFilter.trim())}
        onClear={() => {
          setNameFilter("");
          setAppliedName("");
        }}
      />

      <Paper
        elevation={0}
        sx={{
          p: 2,
          border: "1px solid",
          borderColor: "divider",
          minHeight: 360,
        }}
      >
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        {loading && !roles.length ? (
          <PageLoader text="Loading roles..." />
        ) : !loading && !roles.length ? (
          <EmptyState
            title="No roles found"
            description="No roles found for the current filters."
          />
        ) : (
          <DataTable
            rows={roles}
            columns={columns}
            pageSize={20}
            showPagination
            loading={loading}
            autoHeight
            getRowId={(row) => row.roleId || row.name}
            paginationMode="client"
          />
        )}
      </Paper>

      <RoleFormDialog
        open={formOpen}
        mode="create"
        role={null}
        allowSuggestions={allowSuggestions}
        permissionsLoading={permissionsLoading}
        loading={saving}
        tenantMode={!isSystem}
        onClose={() => setFormOpen(false)}
        onSubmit={handleCreate}
      />
    </Box>
  );
};

export default RoleManagement;
